/**
 * Storefront config: the kill switch.
 *
 * Runs on the merchant's own domain, inside the customer's browser. Nothing here may import
 * from `@/` — the bundle is served from our origin but executes as first-party code on the
 * shop, so it must be self-contained (see `docs/STOREFRONT.md`).
 *
 * The script asks `GET {service}/api/storefront/config?key=peak_pk_…` once per page load and
 * caches the answer in `sessionStorage` for 30 s. The endpoint itself is cacheable for 30 s,
 * so a kill switch flipped in the admin reaches a shopper within ~60 s worst case with no
 * theme edit. The switch is a person's deliberate choice, and an `enabled: false` answer is the
 * only one that leaves a shopper to Shopify's checkout (D385). It is never a fallback: a checkout
 * used while the answer is still loading is held for it (D255), and one used when the config
 * cannot be read goes to the create endpoint, which applies the switch itself (D375,
 * `docs/STOREFRONT.md` §1).
 */

/** What the service tells the storefront. */
export type YcStorefrontConfig = {
  enabled: boolean;
  /**
   * Parsed so an old answer does not break, and ignored since D385: a visible Shop Pay button is a
   * route to Shopify, so the express buttons stay hidden unless `enabled` is `false`.
   */
  hideExpressButtons: boolean;
  /** Canonical origin of the service; the script prefers it over the bootstrap attribute. */
  serviceUrl: string;
  /**
   * The store runs an A/B experiment (D244): experiments switched on and one `RUNNING`. Only then
   * is the `_peak_vid` visitor cookie written or read (`./experiment.ts`). Absent = `false`.
   */
  experiments: boolean;
  /**
   * The store has TrackBee's Tracking SDK on the checkout switched on (D367, provisional): only
   * then does the handoff read TrackBee's visitor id out of `localStorage` and send it
   * (`./trackbee.ts`). Present only when `true`; absent reads as off.
   */
  trackbee?: true;
};

/** How long a cached config is reused before the script asks again. */
export const PEAK_CONFIG_TTL_MS = 30_000;

/** `sessionStorage` key prefix. Namespaced so a theme's own keys can never collide. */
export const PEAK_CONFIG_CACHE_PREFIX = 'peak-checkout:config:';

/**
 * The slice of `sessionStorage` this module uses. Declared structurally so the cache can be
 * unit-tested against a plain object, with no DOM.
 */
export type YcStorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

/** Cache envelope. `at` is `Date.now()` at the time the config was fetched. */
type CacheEnvelope = {
  at: number;
  config: YcStorefrontConfig;
};

export function peakConfigCacheKey(publicKey: string): string {
  return PEAK_CONFIG_CACHE_PREFIX + publicKey;
}

/**
 * Normalise a service URL: trim, drop trailing slashes.
 *
 * Merchants paste this into a theme setting, and `https://checkout.example.com/` plus
 * `/api/checkout/create` would otherwise produce a double slash.
 */
export function normaliseServiceUrl(value: string): string {
  const trimmed = value.trim();
  let end = trimmed.length;
  while (end > 0 && trimmed.charAt(end - 1) === '/') end -= 1;
  return trimmed.slice(0, end);
}

/**
 * The service origin implied by the `<script>` tag's own `src`.
 *
 * This is what makes the one-line install work: a merchant pastes
 * `<script src="https://peakcheckout.com/peak-checkout.js" data-peak-key="…" defer>` and the
 * bundle knows where it came from, so there is no second value to keep in step with the
 * deployment (`docs/STOREFRONT.md` §1).
 *
 * Only `http:` and `https:` yield an origin: a `blob:`, `data:` or extension URL has no usable
 * one (`new URL(...).origin` is the string `"null"` for several of them), and a tag rewritten
 * onto such a URL must fall through to the `data-peak-service` override rather than fetch a
 * config from `https://null`. The caller passes an **absolute** URL — in the DOM that is
 * `script.src`, which the browser has already resolved.
 */
export function originOfScriptSrc(src: string | null): string {
  if (src === null) return '';
  const trimmed = src.trim();
  if (trimmed === '') return '';

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return '';
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
  return url.origin;
}

/**
 * Parse the endpoint's body.
 *
 * Deliberately permissive about everything except `enabled`: the contract's disabled answer is
 * the bare `{ enabled: false }` (the endpoint never leaks whether a key exists), so the other
 * fields must have defaults. Anything that is not an object at all is a broken response
 * and yields `null`, which the caller treats exactly like a network failure: an unreadable config,
 * never a switched-off store.
 */
export function parseConfigResponse(
  value: unknown,
  fallbackServiceUrl: string,
): YcStorefrontConfig | null {
  if (typeof value !== 'object' || value === null) return null;

  const record = value as Record<string, unknown>;
  if (typeof record.enabled !== 'boolean') return null;

  const serviceUrl =
    typeof record.serviceUrl === 'string' && record.serviceUrl.length > 0
      ? normaliseServiceUrl(record.serviceUrl)
      : normaliseServiceUrl(fallbackServiceUrl);

  return {
    enabled: record.enabled,
    hideExpressButtons: record.hideExpressButtons === true,
    serviceUrl,
    experiments: record.experiments === true,
    ...(record.trackbee === true ? { trackbee: true as const } : {}),
  };
}

/**
 * Read the cached config.
 *
 * `maxAgeMs: Infinity` returns the entry whatever its age — that is the "hint" read the script
 * does synchronously at boot so the express-button style can go in before first paint. A stale
 * hint is safe because the real answer, seconds later, either confirms it or removes the style.
 *
 * Every access is wrapped: `sessionStorage` throws in Safari private mode and when the shop
 * sets a restrictive storage policy, and a checkout script may never take the page down with
 * it.
 */
export function readCachedConfig(
  storage: YcStorageLike,
  publicKey: string,
  now: number,
  maxAgeMs: number = PEAK_CONFIG_TTL_MS,
): YcStorefrontConfig | null {
  let raw: string | null;
  try {
    raw = storage.getItem(peakConfigCacheKey(publicKey));
  } catch {
    return null;
  }
  if (raw === null) return null;

  let envelope: unknown;
  try {
    envelope = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof envelope !== 'object' || envelope === null) return null;
  const record = envelope as Partial<CacheEnvelope>;
  if (typeof record.at !== 'number') return null;

  // A clock that moved backwards (system time change, a restored tab) must expire the entry
  // rather than pin it forever.
  const age = now - record.at;
  if (age < 0 || age > maxAgeMs) return null;

  return parseConfigResponse(record.config, '');
}

export function writeCachedConfig(
  storage: YcStorageLike,
  publicKey: string,
  config: YcStorefrontConfig,
  now: number,
): void {
  const envelope: CacheEnvelope = { at: now, config };
  try {
    storage.setItem(peakConfigCacheKey(publicKey), JSON.stringify(envelope));
  } catch {
    // Storage full or blocked: the config is simply re-fetched on the next page load.
  }
}

export function clearCachedConfig(storage: YcStorageLike, publicKey: string): void {
  try {
    storage.removeItem(peakConfigCacheKey(publicKey));
  } catch {
    // Nothing to do — see above.
  }
}

/** The URL the script asks for its config. */
export function configEndpointUrl(serviceUrl: string, publicKey: string): string {
  return (
    normaliseServiceUrl(serviceUrl) + '/api/storefront/config?key=' + encodeURIComponent(publicKey)
  );
}
